import AiIcon from "@shared/svg/ai.svg?react";
import CopyIcon from "@shared/svg/copy.svg?react";
import GlobeIcon from "@shared/svg/globe-icon.svg?react";
import LinkIcon from "@shared/svg/link-icon.svg?react";
import { Avatar } from "@shared/ui/Avatar";
import BodyText, { BODY_TEXT_SIZES } from "@shared/ui/BodyText";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";
import FormSheet from "@shared/ui/FormSheet";
import {
  AsyncMultiSelectDropdown,
  SingleSelectDropdown,
} from "@shared/ui/Dropdowns";
import Headline, { HEADLINE_SIZES } from "@shared/ui/Headline";
import { Input } from "@shared/ui/Inputs";
import Label, { LABEL_SIZES } from "@shared/ui/Label";
import AppModal from "@shared/ui/Modal";
import { useSidebar } from "@shared/ui/SidebarNavigation";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import moment from "moment-timezone";
import { MinusCircle, Plus } from "phosphor-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useIntl as useInternationalization } from "react-intl";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Waypoint } from "react-waypoint";

import Summary from "~/components/aiNotetaker/Summary";
import Title from "~/components/aiNotetaker/Title";
import ContentLoading from "~/components/ContentLoading";
import useAuth from "~/components/general/_use-auth";
import MachineListItem from "~/components/machines/MachineListItem";
import UnderlineTabsWrapper from "~/components/UnderlineTabsWrapper";
import {
  LANGUAGE_CODE_TO_LANGUAGE_NAME_MAP,
  TRANSCRIPTION_STATUS,
} from "~/constants/aiNote";
import { ITEMS_PER_PAGE } from "~/constants/global";
import { AI_NOTETAKER_EVENTS } from "~/constants/mixpanelEvents";
import {
  usePublishAiNote,
  useTranslateAiNoteSummary,
  useUnassignAiNoteFromWorkOrder,
} from "~/services/ai-note";
import {
  useListAllOwnOemAssets,
  useDeleteMachineHistoryNote,
} from "~/services/asset";
import { useListOwnOemTickets } from "~/services/tickets";
import {
  AiNoteType,
  PublishedMachineType,
  PublishedTicketType,
} from "~/types/aiNote";
import { Asset } from "~/types/asset";
import { UserType } from "~/types/user";
import { WaypointEventType } from "~/types/waypointEvent";
import { getMachineThumbnail } from "~/utils/_machines";
import { registerMixpanelEvent } from "~/utils/_mixpanel";
import { groupDataByMonth } from "~/utils/group";

const Component = ({ aiNote }: { aiNote: AiNoteType }) => {
  const navigate = useNavigate();
  const { messages } = useInternationalization();
  const { user } = useAuth() as { user: UserType };

  const dateFormat = user?.dateFormat || "DD.MM.YYYY";
  const timeFormat = user?.timeFormat || "h:mm A";

  const { open: sidebarExpanded } = useSidebar();

  const [state, setState] = useState({
    machines: [],
    publishModalOpen: false,
    publishModalSearchValue: "",
    workOrderSearchValue: "",
    workOrders: [],
  });

  const isSelfCreated = aiNote.createdBy?._id === user?._id;
  // A work order renders the note's summary and transcript, so the server only
  // accepts a note that has finished transcribing.
  const isTranscribed =
    Number(aiNote.transcriptionStatus) === TRANSCRIPTION_STATUS.COMPLETED;

  const {
    assets,
    handleFetchMore: handleAssetsFetchMore,
    assetsListLoading,
    totalCount: assetsTotalCount,
  } = useListAllOwnOemAssets({
    limit: ITEMS_PER_PAGE,
    searchQuery: state.publishModalSearchValue,
    skip: 0,
    skipCondition: !isSelfCreated,
  });

  const {
    tickets: workOrders,
    handleFetchMore: handleWorkOrdersFetchMore,
    loading: workOrdersLoading,
    totalCount: workOrdersTotalCount,
  } = useListOwnOemTickets({
    activeFilters: undefined,
    limit: ITEMS_PER_PAGE,
    searchQuery: state.workOrderSearchValue,
    skip: 0,
    skipCondition: !isSelfCreated,
    where: undefined,
  });

  const { removeHistoryNote, loading: isDeletingMachineNote } =
    useDeleteMachineHistoryNote();
  const { publishNote, isPublishing } = usePublishAiNote();
  const { unassignNote, isUnassigning } = useUnassignAiNoteFromWorkOrder();

  const groupedPublishedMachines = useMemo(() => {
    const sortedPublishedMachines = aiNote?.publishedMachines
      ?.filter((publishedMachine) => publishedMachine.machine)
      ?.sort(
        (a, b) =>
          new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
      );

    return groupDataByMonth(sortedPublishedMachines, "publishedAt");
  }, [aiNote?.publishedMachines]);

  const publishedWorkOrders = useMemo(
    () =>
      (aiNote?.publishedTickets ?? [])
        .filter((publishedTicket) => publishedTicket.ticket)
        .sort(
          (a, b) =>
            new Date(b.publishedAt).getTime() -
            new Date(a.publishedAt).getTime(),
        ),
    [aiNote?.publishedTickets],
  );

  const allWorkOrderOptions = workOrders?.map(
    (workOrder: { _id: string; title: string; ticketId: string }) => ({
      ...workOrder,
      value: workOrder._id,
      label: workOrder.title,
      description: workOrder.ticketId,
    }),
  );

  const allMachineOptions = assets?.map((machine: Asset) => {
    return {
      ...machine,
      value: machine._id,
      label: machine.name,
      description: machine?.serialNumber,
      thumbnail: getMachineThumbnail({
        oem: machine.oem,
        image: machine.image,
      }),
    };
  });

  const handleMachineHistoryNoteDelete = (
    machineHistoryNoteId: string,
    machineId: string,
  ) => {
    removeHistoryNote(machineHistoryNoteId, machineId, aiNote._id)
      .then(() => {
        toast(
          <Toast message={messages["aiNotetaker"]["delete-note-success"]} />,
          {
            closeButton: false,
          },
        );
      })
      .catch((error) => {
        toast(
          <Toast
            message={
              error?.message || messages["aiNotetaker"]["delete-note-failed"]
            }
            type={TOAST_TYPES.ERROR}
          />,
          {
            closeButton: false,
          },
        );
      });
  };

  const handleWorkOrderUnassign = (ticketId: string) => {
    unassignNote({ aiNoteId: aiNote._id, ticketId })
      .then(() => {
        toast(<Toast message={messages["aiNotetaker"]["note-unassigned"]} />, {
          closeButton: false,
        });
      })
      .catch((error) => {
        toast(
          <Toast
            message={
              error?.message || messages["common"]["errors"]["somethingWrong"]
            }
            type={TOAST_TYPES.ERROR}
          />,
          {
            closeButton: false,
          },
        );
      });
  };

  const handleWorkOrdersScrollBottom = (event: WaypointEventType) =>
    workOrders.length >= ITEMS_PER_PAGE &&
    !workOrdersLoading &&
    event.previousPosition !== Waypoint.above &&
    workOrdersTotalCount > workOrders.length &&
    handleWorkOrdersFetchMore({
      limit: ITEMS_PER_PAGE,
      skip: workOrders.length,
    });

  const handleMachinesScrollBottom = (event: WaypointEventType) =>
    assets.length >= ITEMS_PER_PAGE &&
    !assetsListLoading &&
    event.previousPosition !== Waypoint.above &&
    assetsTotalCount > assets.length &&
    handleAssetsFetchMore({
      limit: ITEMS_PER_PAGE,
      skip: assets.length,
    });

  const handlePublishSummary = () => {
    registerMixpanelEvent(AI_NOTETAKER_EVENTS.PUBLISHING_AI_NOTE);
    publishNote({
      id: aiNote._id,
      machines: state.machines?.map((machine) => machine._id),
      tickets: state.workOrders?.map((workOrder) => workOrder._id),
    })
      .then(() => {
        resetPublishModalState();
        toast(<Toast message={messages["aiNotetaker"]["note-published"]} />, {
          closeButton: false,
        });
      })
      .catch((error) => {
        toast(
          <Toast
            message={
              error?.message || messages["aiNotetaker"]["publish-failed"]
            }
            type={TOAST_TYPES.ERROR}
          />,
          {
            closeButton: false,
          },
        );
      });
  };

  const resetPublishModalState = () => {
    setState((prevState) => ({
      ...prevState,
      machines: [],
      publishModalOpen: false,
      publishModalSearchValue: "",
      workOrderSearchValue: "",
      workOrders: [],
    }));
  };

  const handleSearchQuery = (value: string) => {
    if (state.publishModalSearchValue !== value) {
      setState((prevState) => ({
        ...prevState,
        publishModalSearchValue: value,
      }));
    }
  };

  const handleWorkOrderSearchQuery = (value: string) => {
    if (state.workOrderSearchValue !== value) {
      setState((prevState) => ({
        ...prevState,
        workOrderSearchValue: value,
      }));
    }
  };

  return (
    <div className="flex-col gap-y-lg hidden lg:flex w-4/12">
      <audio src={aiNote.audioUrl} controls className="w-full" />

      {user && isSelfCreated && (
        <Fragment>
          <div className="flex flex-col gap-y-md p-lg border border-solid border-primary rounded-lg h-full">
            <Label size={LABEL_SIZES.SMALL}>
              {messages.aiNotetaker["published-to"]}
            </Label>
            <Button
              className="w-fit"
              // @ts-ignore
              leadingIcon={<Plus size={16} weight="bold" />}
              onClick={() =>
                setState((prev) => ({ ...prev, publishModalOpen: true }))
              }
              text={messages.aiNotetaker["drawer-publish"]}
              variant={BUTTON_VARIANTS.LINK}
            />
            {publishedWorkOrders.length > 0 && (
              <div className="flex flex-col gap-y-sm">
                <Label className="text-secondary" size={LABEL_SIZES.SMALL}>
                  {messages.aiNotetaker["work-orders-published-to"]}
                </Label>
                <div className="flex flex-col gap-y-md max-h-[180px] overflow-y-auto no-scrollbar">
                  {publishedWorkOrders.map(
                    ({ publishedAt, ticket }: PublishedTicketType) => (
                      <div
                        className="flex items-center gap-x-sm p-md border border-solid border-primary rounded-lg cursor-pointer"
                        key={ticket._id}
                        onClick={() =>
                          navigate(`/app/work/work-orders/${ticket._id}`)
                        }
                      >
                        <div className="flex flex-col w-full min-w-0 gap-y-2xs">
                          <Headline
                            className="w-full truncate"
                            size={HEADLINE_SIZES.X_SMALL}
                          >
                            {ticket.title}
                          </Headline>
                          <BodyText
                            color="text-secondary w-full truncate"
                            size={BODY_TEXT_SIZES.X_SMALL}
                          >
                            {moment(publishedAt).format(
                              `${dateFormat} • ${timeFormat}`,
                            )}{" "}
                            &#8226; #{ticket.ticketId}
                          </BodyText>
                        </div>
                        <div className="flex-shrink-0 flex items-center">
                          {/* @ts-ignore */}
                          <MinusCircle
                            onClick={(e) => {
                              e.stopPropagation();
                              !isUnassigning &&
                                handleWorkOrderUnassign(ticket._id);
                            }}
                            className={`h-2xl w-2xl ml-auto ${
                              isUnassigning
                                ? "text-disabled cursor-not-allowed"
                                : "text-tertiary cursor-pointer"
                            }`}
                          />
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </div>
            )}
            <div className="flex flex-col max-h-[310px] overflow-y-auto no-scrollbar">
              {!groupedPublishedMachines.length &&
              !publishedWorkOrders.length ? (
                <BodyText
                  className="text-center"
                  color="text-secondary"
                  size={BODY_TEXT_SIZES.X_SMALL}
                >
                  {messages.aiNotetaker["drawer-published-tab-empty"]}
                </BodyText>
              ) : (
                groupedPublishedMachines.map(
                  ({
                    date,
                    data,
                  }: {
                    date: string;
                    data: Array<PublishedMachineType>;
                  }) => (
                    <div key={date} className="flex flex-col w-full gap-y-sm">
                      <div className="flex items-center gap-x-sm pt-sm pb-xs">
                        <p className="m-0 text-disabled">&#8226;</p>
                        <Label
                          className="text-secondary"
                          size={LABEL_SIZES.SMALL}
                        >
                          {date}
                        </Label>
                      </div>
                      <div className="flex flex-col gap-y-md">
                        {data
                          .sort(
                            (a, b) =>
                              moment(b.publishedAt).valueOf() -
                              moment(a.publishedAt).valueOf(),
                          )
                          .map(
                            ({
                              machine,
                              machineHistoryNoteId,
                              publishedAt,
                            }) => (
                              <div
                                className="flex items-center gap-x-sm p-md border border-solid border-primary rounded-lg cursor-pointer"
                                key={machineHistoryNoteId}
                                onClick={() =>
                                  navigate(
                                    `/app/assets/machines/${machine._id}`,
                                  )
                                }
                              >
                                <div className="shrink-0">
                                  <MachineListItem.Thumbnail
                                    machine={{
                                      ...machine,
                                      oem: user?.oem,
                                    }}
                                    variant="x-small"
                                  />
                                </div>
                                <div
                                  className={`flex flex-col ${
                                    sidebarExpanded
                                      ? "lg:w-3/5 xl:w-[73%]"
                                      : "lg:w-8/12 xl:w-4/5"
                                  } gap-y-2xs`}
                                >
                                  <Headline
                                    className="w-full truncate"
                                    size={HEADLINE_SIZES.X_SMALL}
                                  >
                                    {machine.name}
                                  </Headline>
                                  <BodyText
                                    color="text-secondary w-full truncate"
                                    size={BODY_TEXT_SIZES.X_SMALL}
                                  >
                                    {moment(publishedAt).format(
                                      `${dateFormat} • ${timeFormat}`,
                                    )}{" "}
                                    &#8226; SN: {machine.serialNumber}
                                  </BodyText>
                                </div>
                                <div className="flex-shrink-0 flex-grow flex items-center">
                                  {/* @ts-ignore */}
                                  <MinusCircle
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      !isDeletingMachineNote &&
                                        handleMachineHistoryNoteDelete(
                                          machineHistoryNoteId,
                                          machine._id,
                                        );
                                    }}
                                    className={`h-2xl w-2xl ml-auto ${
                                      isDeletingMachineNote
                                        ? "text-disabled cursor-not-allowed"
                                        : "text-tertiary cursor-pointer"
                                    }`}
                                  />
                                </div>
                              </div>
                            ),
                          )}
                      </div>
                    </div>
                  ),
                )
              )}
            </div>
          </div>

          <FormSheet
            open={state.publishModalOpen}
            onClose={() => resetPublishModalState()}
            onCancel={() => resetPublishModalState()}
            onSubmit={() => handlePublishSummary()}
            title={messages.aiNotetaker["modal-publish-summary"]}
            bodyClassName="flex flex-col gap-y-2xl"
            submitDisabled={
              isPublishing ||
              (state.machines.length === 0 && state.workOrders.length === 0)
            }
            cancelText={messages.common["cancel"]}
            submitText={messages.common["linkInsert"]["confirm"]}
          >
            <div className="flex flex-col">
              <AsyncMultiSelectDropdown
                placeholder={
                  messages["hierarchy"]["assets"]["dropDown"]["placeholder"]
                }
                label={messages["hierarchy"]["assets"]["dropDown"]["label"]}
                dropdownTitle={
                  messages["teams"]["createTeamDrawer"]["selectMachine"][
                    "placeholderAsset"
                  ]
                }
                showSearchInput
                searchPlaceholder={messages["common"]["searchText"]}
                options={allMachineOptions}
                allowMulti
                setSelectedData={(data) =>
                  setState((prev) => ({
                    ...prev,
                    machines: data ?? [],
                  }))
                }
                setSearchQuery={handleSearchQuery}
                onScrollToBottom={handleMachinesScrollBottom}
                isLoading={assetsListLoading}
                showRemoveAllButton
                variant="chips"
              />
            </div>
            <div className="flex flex-col">
              {!isTranscribed && (
                <BodyText color="text-secondary" size={BODY_TEXT_SIZES.X_SMALL}>
                  {messages.aiNotetaker["work-orders-need-transcript"]}
                </BodyText>
              )}
              {isTranscribed && (
                <AsyncMultiSelectDropdown
                  placeholder={messages["tickets"]["searchPlaceholder"]}
                  label={messages.aiNotetaker["work-orders-published-to"]}
                  dropdownTitle={
                    messages.aiNotetaker["work-orders-published-to"]
                  }
                  showSearchInput
                  searchPlaceholder={messages["common"]["searchText"]}
                  options={allWorkOrderOptions}
                  allowMulti
                  setSelectedData={(data) =>
                    setState((prev) => ({
                      ...prev,
                      workOrders: data ?? [],
                    }))
                  }
                  setSearchQuery={handleWorkOrderSearchQuery}
                  onScrollToBottom={handleWorkOrdersScrollBottom}
                  isLoading={workOrdersLoading}
                  showRemoveAllButton
                  variant="chips"
                />
              )}
            </div>
          </FormSheet>
        </Fragment>
      )}
    </div>
  );
};

const AiNote = ({
  aiNote,
  isWorkOrderView = false,
}: {
  aiNote: AiNoteType;
  /**
   * Opened from a work order rather than from the viewer's own notes. The note
   * is a record attached to that work order there, not something the viewer
   * manages, so publishing, sharing and the recording are all left out.
   */
  isWorkOrderView?: boolean;
}) => {
  const { messages } = useInternationalization();
  const { user } = useAuth() as { user: UserType };

  const dateFormat = user?.dateFormat || "DD.MM.YYYY";

  const [state, setState] = useState<{
    shareModalOpen: boolean;
    summary: string;
    targetLanguageCode: {
      label: string;
      value: string;
    };
  }>({
    shareModalOpen: false,
    summary: aiNote.summary,
    targetLanguageCode: null,
  });
  const [copyEditorContent, setCopyEditorContent] = useState<
    (() => void) | null
  >(null);

  const isSelfCreated = aiNote.createdBy?._id === user?._id;

  const { translateSummary, isTranslating } = useTranslateAiNoteSummary();

  useEffect(() => {
    setState((prev) => ({ ...prev, summary: aiNote.summary }));
  }, [aiNote.summary]);

  useEffect(() => {
    state.targetLanguageCode &&
      translateSummary({
        aiNoteId: aiNote._id,
        targetLanguageCode: state.targetLanguageCode.value,
      })
        .then((summary: { data: { translateAiNoteSummary: string } }) => {
          setState((prev) => ({
            ...prev,
            summary: summary.data.translateAiNoteSummary,
          }));
        })
        .catch((error) => {
          setState((prev) => ({
            ...prev,
            targetLanguageCode: null,
          }));
          toast(
            <Toast
              message={
                error?.message || messages["aiNotetaker"]["translation-failed"]
              }
              type={TOAST_TYPES.ERROR}
            />,
            {
              closeButton: false,
            },
          );
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.targetLanguageCode]);

  const languageOptions = useMemo(
    () =>
      Object.keys(LANGUAGE_CODE_TO_LANGUAGE_NAME_MAP).map((languageCode) => ({
        label: LANGUAGE_CODE_TO_LANGUAGE_NAME_MAP[languageCode],
        value: languageCode,
      })),
    [],
  );

  return (
    <div className="p-2xl flex flex-col gap-y-lg">
      <div className="flex w-full items-center gap-x-sm">
        <div className="flex-shrink-0 flex items-center justify-center rounded-full w-5xl h-5xl bg-violet-lightest">
          <AiIcon className="h-3xl w-3xl text-violet" />
        </div>
        <Title
          isSelfCreated={isSelfCreated}
          noteId={aiNote._id}
          title={aiNote.title}
        />
        {!isWorkOrderView && (
          <Fragment>
            <Button
              className="hidden sm:flex"
              leadingIcon={<LinkIcon className="-rotate-45 text-inverse" />}
              onClick={() =>
                setState((prev) => ({ ...prev, shareModalOpen: true }))
              }
              text={messages.aiNotetaker["share"]}
            />
            <Button
              className="flex sm:hidden"
              leadingIcon={<LinkIcon className="-rotate-45 text-inverse" />}
              onClick={() =>
                setState((prev) => ({ ...prev, shareModalOpen: true }))
              }
            />
          </Fragment>
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-y-md sm:gap-y-0 sm:gap-x-md">
        <div className="flex justify-between sm:justify-normal items-center sm:items-start sm:flex-col sm:gap-y-md">
          <BodyText color="text-secondary" size={BODY_TEXT_SIZES.X_SMALL}>
            {messages.aiNotetaker["drawer-created-on"]}:
          </BodyText>
          <BodyText
            className="flex items-center h-2xl"
            size={BODY_TEXT_SIZES.X_SMALL}
          >
            {moment(aiNote.createdAt).format(`${dateFormat}`)}
          </BodyText>
        </div>
        <div className="flex justify-between sm:justify-normal items-center sm:items-start sm:flex-col sm:gap-y-md">
          <BodyText color="text-secondary" size={BODY_TEXT_SIZES.X_SMALL}>
            {messages.aiNotetaker["drawer-created-by"]}:
          </BodyText>
          <div className="flex items-center gap-x-md">
            <Avatar name={aiNote.createdBy.name} />
            <BodyText>{aiNote.createdBy.name}</BodyText>
          </div>
        </div>
      </div>
      {!isWorkOrderView && (
        <audio
          src={aiNote.audioUrl}
          controls
          className="flex w-full lg:hidden"
        />
      )}
      <UnderlineTabsWrapper
        className="max-lg:!px-0"
        tabsClassName="flex flex-col pb-0"
        tabs={[
          {
            id: "summary",
            className: "max-lg:!px-0",
            label: messages.aiNotetaker["drawer-summary"],
            content: (
              <div className="flex w-full gap-x-xl">
                <div
                  className={`flex flex-col gap-y-md border border-solid border-primary rounded-lg p-lg w-full ${
                    isWorkOrderView ? "" : "lg:w-8/12"
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-y-sm sm:gap-y-0 sm:gap-x-sm">
                    <Button
                      className="w-fit"
                      disabled={state.summary.length === 0 || isTranslating}
                      leadingIcon={
                        <CopyIcon
                          className={
                            state.summary.length === 0 || isTranslating
                              ? "text-inverse"
                              : "text-brand"
                          }
                        />
                      }
                      onClick={() => {
                        copyEditorContent?.();
                        toast(
                          <Toast
                            message={messages.aiNotetaker["summary-copied"]}
                            type={TOAST_TYPES.SUCCESS}
                          />,
                          {
                            closeButton: false,
                          },
                        );
                      }}
                      text={messages.aiNotetaker["copy-summary"]}
                      variant={BUTTON_VARIANTS.SECONDARY}
                    />
                    {user && (
                      <div className="w-44xl">
                        <SingleSelectDropdown
                          keyId="deepl-language"
                          placeholder={messages.aiNotetaker["translate-into"]}
                          options={languageOptions}
                          searchable
                          disabled={isTranslating || !user}
                          // @ts-ignore
                          searchBy="label"
                          values={
                            state.targetLanguageCode
                              ? [state.targetLanguageCode]
                              : []
                          }
                          onChange={(val: { label: string; value: string }[]) =>
                            setState((prev) => ({
                              ...prev,
                              targetLanguageCode: val[0],
                            }))
                          }
                        />
                      </div>
                    )}
                  </div>
                  {isTranslating ? (
                    <div className="flex items-center justify-center w-full h-full">
                      <ContentLoading />
                    </div>
                  ) : (
                    <Summary
                      key={state.summary.length}
                      isSelfCreated={isSelfCreated}
                      noteId={aiNote._id}
                      setCopyEditorContent={setCopyEditorContent}
                      summary={state.summary}
                    />
                  )}
                </div>
                {!isWorkOrderView && <Component aiNote={aiNote} />}
              </div>
            ),
          },
          {
            id: "transcript",
            className: "max-lg:!px-0",
            label: messages.aiNotetaker["drawer-transcript"],
            content: (
              <div className="flex w-full gap-x-xl">
                <div
                  className={`flex flex-col gap-y-md border border-solid border-primary rounded-lg p-lg w-full ${
                    isWorkOrderView ? "" : "lg:w-8/12"
                  }`}
                >
                  <Button
                    className="w-fit"
                    leadingIcon={<CopyIcon className="text-brand" />}
                    onClick={() => {
                      navigator.clipboard.writeText(aiNote.transcript);
                      toast(
                        <Toast
                          message={messages.aiNotetaker["transcript-copied"]}
                          type={TOAST_TYPES.SUCCESS}
                        />,
                        {
                          closeButton: false,
                        },
                      );
                    }}
                    text={messages.aiNotetaker["copy-transcript"]}
                    variant={BUTTON_VARIANTS.SECONDARY}
                  />
                  <BodyText
                    className="max-h-[380px] overflow-y-auto"
                    size={BODY_TEXT_SIZES.X_SMALL}
                  >
                    {aiNote.transcript}
                  </BodyText>
                </div>
                {!isWorkOrderView && <Component aiNote={aiNote} />}
              </div>
            ),
          },
        ]}
      />

      {!isWorkOrderView && (
        <AppModal
          content={
            <div className="flex flex-col gap-y-2xs">
              <div className="flex items-center w-full gap-x-md">
                <Input
                  wrapperClasses="flex-grow"
                  value={`${window.location.origin}/ai/note/${aiNote._id}`}
                  disabled
                />
                <Button
                  leadingIcon={
                    <CopyIcon className="flex-shrink-0 text-inverse" />
                  }
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${window.location.origin}/ai/note/${aiNote._id}`,
                    );
                    toast(
                      <Toast
                        message={messages.aiNotetaker["link-copied"]}
                        type={TOAST_TYPES.SUCCESS}
                      />,
                      {
                        closeButton: false,
                      },
                    );
                  }}
                  text={messages.aiNotetaker["copy-link"]}
                />
              </div>
              <div className="flex items-center gap-x-2xs">
                <GlobeIcon className="w-lg h-lg text-secondary" />
                <BodyText color="text-secondary" size={BODY_TEXT_SIZES.X_SMALL}>
                  {messages.aiNotetaker["copy-link-description"]}
                </BodyText>
              </div>
            </div>
          }
          footer={<></>}
          handleClose={() =>
            setState((prev) => ({ ...prev, shareModalOpen: false }))
          }
          isOpen={state.shareModalOpen}
          maxWidth="!max-w-container-xl"
          title={messages.aiNotetaker["share"]}
        />
      )}
    </div>
  );
};

export default AiNote;
